import { SessionProvider } from "next-auth/react";
import { DefaultSeo } from "next-seo";
import { AppProps } from "next/app";
import Head from "next/head";
import dynamic from "next/dynamic";
import { PostHogProvider } from "~/modules/analytics/components/PostHogProvider";
import { ReduxProvider } from "~/modules/app/components/ReduxProvider";
import { TRPCReactProvider } from "~/modules/app/components/TRPCReactProvider";
import { InlineAuthProvider } from "~/modules/auth/components/InlineAuthProvider";
import { ToasterLayout } from "~/modules/toaster/components/ToasterLayout";
import { GlobalRegistrationModal } from "~/modules/auth/components/GlobalRegistrationModal";
import { GlobalChatDockLayout } from "~/modules/asset_chat/components/GlobalChatDock/GlobalChatDockLayout";
import { UserLoader } from "~/modules/user/components/UserLoader";
import { ConsoleMessage, IntercomScript } from "../components/Head";
import "../styles/tailwind.css";
import "../styles/typography.css";
import { SearchInitializer } from "~/modules/search/components/SearchInitializer";
import { SolanaWalletProvider } from "~/modules/swap";
import { ThemeProvider } from "~/components/ThemeProvider";
import { SOCIAL_OG_IMAGE_URL } from "~/consts";
import { AuthRedirectManager } from "~/modules/auth/components/AuthRedirectManager";

import { useMemo, type JSX } from "react";

// // // //

const SpeedInsights = dynamic(
    () => import("@vercel/speed-insights/next").then((m) => m.SpeedInsights),
    { ssr: false },
);

function MyApp({ Component, pageProps }: AppProps): JSX.Element {
    // Memoize the page element so React can skip re-rendering the page when MyApp
    // re-renders with the same Component + pageProps. Next.js re-renders MyApp after
    // hydration on every getStaticProps page (shallow `router.replace` triggered by
    // the rewrites in next.config.js) - see node_modules/next/dist/client/index.js
    // NOTE - pages that read the router via useRouter still update through context
    const page = useMemo(
        () => <Component {...pageProps} />,
        [Component, pageProps],
    );

    return (
        <>
            <Head>
                {/* NOTE - Next.js warns against putting <meta name="viewport"> in _document.tsx; it must live in _app.tsx or per-page */}
                <meta
                    name="viewport"
                    content="width=device-width, initial-scale=1"
                />

                {/* react-scan */}
                {/* Comment this in for performance monitoring */}
                {/* <script
                    crossOrigin="anonymous"
                    src="//unpkg.com/react-scan/dist/auto.global.js"
                /> */}

                {/* OpenGraph Meta */}
                {/* NOTE - remove this OpenGraph Meta after confirming that the DefaultSeo component below is working as-expected */}
                {/* <meta
                    name="description"
                    content="rwa.xyz provides analytics on Tokenized Real-World Assets"
                />
                <meta property="og:title" content="rwa.xyz" />
                <meta
                    property="og:description"
                    content="rwa.xyz provides analytics on Tokenized Real-World Assets"
                />
                <meta property="og:url" content="https://app.rwa.xyz" />
                <meta property="og:type" content="website" />
                <meta
                    property="og:image"
                    content={SOCIAL_OG_IMAGE_URL}
                />
                <meta property="og:image:alt" content="rwa.xyz" />
                <meta property="og:image:width" content="4000" />
                <meta property="og:image:height" content="2300" /> */}
            </Head>

            {/* Define default SEO content for the app */}
            <DefaultSeo
                titleTemplate="RWA.xyz | %s"
                description="RWA.xyz provides analytics on Tokenized Real-World Assets"
                openGraph={{
                    type: "website",
                    url: "https://app.rwa.xyz",
                    title: "RWA.xyz",
                    site_name: "RWA.xyz",
                    description:
                        "RWA.xyz provides analytics on Tokenized Real-World Assets",
                    images: [
                        {
                            url: SOCIAL_OG_IMAGE_URL,
                            width: 4000,
                            height: 2300,
                            alt: "rwa.xyz",
                        },
                    ],
                }}
                twitter={{
                    handle: "@rwa_xyz",
                    site: "@rwa_xyz",
                    cardType: "summary_large_image",
                }}
                additionalMetaTags={[
                    {
                        name: "robots",
                        content: "index, follow",
                    },
                    {
                        property: "og:locale",
                        content: "en_US",
                    },
                ]}
                // additionalLinkTags={
                //     [
                //         // {
                //         //     rel: "icon",
                //         //     href: "https://castle.finance/favicon.ico",
                //         // },
                //         // {
                //         //     rel: "apple-touch-icon",
                //         //     href: "castle-logo.svg",
                //         //     sizes: "76x76",
                //         // },
                //         // {
                //         //     rel: "preload",
                //         //     href: "https://www.test.ie/font/sample-font.woof2",
                //         //     as: "font",
                //         //     type: "font/woff2",
                //         //     crossOrigin: "anonymous",
                //         // },
                //     ]
                // }
            />

            {/* Load console message */}
            <ConsoleMessage />

            <ThemeProvider
                attribute="class"
                defaultTheme="light"
                enableSystem
                disableTransitionOnChange
            >
                <SessionProvider session={pageProps.session}>
                    <PostHogProvider>
                        <ReduxProvider>
                            {/* NOTE - TRPCReactProvider must wrap everything that uses tRPC or react-query hooks, */}
                            {/* including the global layouts below (i.e. GlobalChatDockLayout) */}
                            <TRPCReactProvider>
                                <SolanaWalletProvider>
                                    <SearchInitializer>
                                        {/* NOTE - we must keep UserLoader here because its */}
                                        {/* responsible for firing off the user authentication handshake */}
                                        <UserLoader>
                                            <>
                                                {/* Load Vercel Speed Insights */}
                                                <SpeedInsights />

                                                {/* Load Intercom */}
                                                <IntercomScript />

                                                {/* Inline auth provider renders authentication and onboarding modals that manage their own visibility */}
                                                <InlineAuthProvider>
                                                    {page}
                                                </InlineAuthProvider>

                                                {/* Handle post-authentication redirect */}
                                                <AuthRedirectManager />
                                            </>
                                        </UserLoader>
                                    </SearchInitializer>
                                </SolanaWalletProvider>

                                <ToasterLayout />
                                <GlobalRegistrationModal />
                                <GlobalChatDockLayout />
                            </TRPCReactProvider>
                        </ReduxProvider>
                    </PostHogProvider>
                </SessionProvider>
            </ThemeProvider>
        </>
    );
}

export default MyApp;
